import cx from 'classnames'
import {
  type CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as THREE from 'three'
import { debounce } from 'throttle-debounce'

import {
  type FrameViewerCamera,
  createCamera,
  fitCameraToObject,
} from '../lib/frame-viewer/camera'
import { generateFrame, updateFrame } from '../lib/frame-viewer/frame-generator'
import { getObjectSizeVector } from '../lib/frame-viewer/helpers'
import { generateTextureUrlFromImage } from '../lib/frame-viewer/image'
import {
  createAmbientLight,
  createDirectionalLight,
} from '../lib/frame-viewer/light'
import { createRenderer } from '../lib/frame-viewer/renderer'
import { createScene } from '../lib/frame-viewer/scene'
import { type FrameSettings } from '../lib/frame-viewer/settings'
import { UVMapViewer } from '../lib/frame-viewer/uv-map-viewer'
import { getWallTexture, wallHeight, wallWidth } from '../lib/frame-viewer/wall'
import { useForwardRef } from '../lib/hooks'
import { useSpring } from '../lib/spring'

interface FrameViewerProps {
  frameSettings: FrameSettings
  camera: FrameViewerCamera
  width?: number
  height?: number
  backgroundColor?: string
  // Set this to true, to enable UV debugger for frame
  debugFrameUv?: boolean
  // Set this to true, to enable UV debugger for artwork
  debugArtworkUv?: boolean
  className?: string
  style?: CSSProperties
}

const FrameViewer = forwardRef<HTMLCanvasElement, FrameViewerProps>(
  (
    {
      frameSettings,
      camera,
      width,
      height,
      backgroundColor,
      debugFrameUv = false,
      debugArtworkUv = false,
      className,
      style,
    },
    ref,
  ) => {
    const canvasRef = useForwardRef<HTMLCanvasElement | null>(ref, null)
    const textureLoaderRef = useRef(new THREE.TextureLoader())
    const sceneRef = useRef<THREE.Scene>()
    const cameraRef = useRef<THREE.PerspectiveCamera>()
    const rendererRef = useRef<THREE.WebGLRenderer>()
    const directionalLightRef = useRef<THREE.DirectionalLight>()

    const frameGeometryRef = useRef<THREE.Geometry>()
    const frameMaterialRef = useRef<THREE.MeshStandardMaterial>()
    const frameMeshRef = useRef<THREE.Mesh>()

    const passepartoutGeometryRef = useRef<THREE.Geometry>()
    const passepartoutMaterialRef = useRef<THREE.MeshBasicMaterial>()
    const passepartoutMeshRef = useRef<THREE.Mesh>()

    const artworkGeometryRef = useRef<THREE.Geometry>()
    const artworkMaterialRef = useRef<THREE.MeshStandardMaterial>()
    const artworkMeshRef = useRef<THREE.Mesh>()

    const wallMeshRef = useRef<THREE.Mesh>()
    const wallMaterialRef = useRef<THREE.MeshBasicMaterial>()
    const wallGeometryRef = useRef<THREE.PlaneGeometry>()

    const frameUvViewerRef = useRef<UVMapViewer>()
    const artworkUvViewerRef = useRef<UVMapViewer>()

    const [isReady, setIsReady] = useState(false)
    const [artworkUrl, setArtworkUrl] = useState<string | null>(
      frameSettings.artworkUrl ?? null,
    )
    const [artworkTexture, setArtworkTexture] = useState<THREE.Texture | null>(
      null,
    )
    const [frameDiffuseUrl, setFrameDiffuseUrl] = useState<string | undefined>(
      frameSettings.frameTexture?.diffuseUrl,
    )
    const [frameDiffuseTexture, setFrameDiffuseTexture] =
      useState<THREE.Texture | null>(null)

    // Camera zoom and rotation
    const [maxDimensionTarget, setMaxDimensionTarget] = useState(
      Math.max(
        frameSettings.artworkWidth +
          frameSettings.passepartoutLeftSize +
          frameSettings.passepartoutRightSize +
          frameSettings.borderLeftSize +
          frameSettings.borderRightSize,
        frameSettings.artworkHeight +
          frameSettings.passepartoutTopSize +
          frameSettings.passepartoutBottomSize +
          frameSettings.borderTopSize +
          frameSettings.borderBottomSize,
      ),
    )
    const [rotationTarget, setRotationTarget] = useState(0)
    const maxDimension = useSpring(maxDimensionTarget, 8, 10)
    const rotation = useSpring(rotationTarget, 1, 10)

    const wallTexture = useMemo(() => {
      if (!isReady) {
        return null
      }

      return getWallTexture(frameSettings, backgroundColor)
    }, [backgroundColor, frameSettings, isReady])

    const fitCameraToFrame = useCallback(() => {
      if (!cameraRef.current || !frameMeshRef.current) {
        return
      }

      fitCameraToObject(
        cameraRef.current,
        frameMeshRef.current,
        maxDimension,
        camera.fitRatio,
        THREE.MathUtils.degToRad(rotation),
        camera.fitPerRad,
      )
    }, [camera, maxDimension, rotation])

    const debounceUpdateArtworkTexture = useRef(
      debounce(400, async (frameSettings: FrameSettings) => {
        if (!frameSettings.artworkUrl) {
          return
        }

        const textureUrl = await generateTextureUrlFromImage(
          frameSettings.artworkUrl,
          frameSettings.artworkWidth,
          frameSettings.artworkHeight,
        )

        textureLoaderRef.current.load(textureUrl, (texture) => {
          texture.encoding = THREE.sRGBEncoding

          setArtworkUrl(textureUrl)
          setArtworkTexture(texture)

          if (debugArtworkUv && artworkUvViewerRef.current) {
            artworkUvViewerRef.current.setTexture(texture)
          }
        })
      }),
    )

    const debouncedSetMaxDimensionTarget = useRef(
      debounce(400, async (maxDimensionTarget: number) => {
        setMaxDimensionTarget(maxDimensionTarget)
      }),
    )

    const updateFrameTexture = useCallback(
      (forceUpdate = false) => {
        // Unset frame texture
        if (!frameSettings.frameTexture?.diffuseUrl) {
          setFrameDiffuseUrl('')
          setFrameDiffuseTexture(null)
          return
        }

        // Set frame texture
        if (
          forceUpdate ||
          !frameDiffuseTexture ||
          frameDiffuseUrl !== frameSettings.frameTexture.diffuseUrl
        ) {
          textureLoaderRef.current.load(
            frameSettings.frameTexture.diffuseUrl,
            (texture) => {
              texture.encoding = THREE.sRGBEncoding
              texture.wrapS = THREE.MirroredRepeatWrapping
              texture.wrapT = THREE.MirroredRepeatWrapping

              setFrameDiffuseUrl(frameSettings.frameTexture?.diffuseUrl)
              setFrameDiffuseTexture(texture)

              if (debugFrameUv && frameUvViewerRef.current) {
                frameUvViewerRef.current.setTexture(texture)
              }
            },
          )
        }
      },
      [
        debugFrameUv,
        frameDiffuseTexture,
        frameDiffuseUrl,
        frameSettings.frameTexture,
      ],
    )

    // Initialize scene primitives (re-initialize on resize)
    useEffect(() => {
      if (!canvasRef.current || !width || !height) {
        setIsReady(false)
        return
      }

      // Create scene, camera and renderer
      sceneRef.current = createScene()
      const canvasAspectRatio =
        canvasRef.current.width / canvasRef.current.height
      cameraRef.current = createCamera(canvasAspectRatio, camera.settings)
      rendererRef.current = createRenderer(
        canvasRef.current,
        width,
        height,
        window.devicePixelRatio,
      )

      setIsReady(true)

      // Create lights
      const ambientLight = createAmbientLight()
      directionalLightRef.current = createDirectionalLight()
      sceneRef.current.add(ambientLight)
      sceneRef.current.add(directionalLightRef.current)

      // Create geometries
      frameGeometryRef.current = new THREE.Geometry()
      passepartoutGeometryRef.current = new THREE.Geometry()
      artworkGeometryRef.current = new THREE.Geometry()

      generateFrame(
        frameGeometryRef.current,
        passepartoutGeometryRef.current,
        artworkGeometryRef.current,
        frameSettings,
      )

      // Create materials
      frameMaterialRef.current = new THREE.MeshStandardMaterial({
        color: 'black',
        roughness: 1,
        metalness: 0,
      })

      if (
        frameSettings.passepartoutTopSize > 0 ||
        frameSettings.passepartoutRightSize > 0 ||
        frameSettings.passepartoutBottomSize > 0 ||
        frameSettings.passepartoutLeftSize > 0
      ) {
        passepartoutMaterialRef.current = new THREE.MeshBasicMaterial({
          color: frameSettings.passepartoutColor,
          reflectivity: 0,
        })
      }

      artworkMaterialRef.current = new THREE.MeshStandardMaterial({
        color: 'white',
        roughness: 1,
        metalness: 0,
      })

      // Create meshes
      frameMeshRef.current = new THREE.Mesh(
        frameGeometryRef.current,
        frameMaterialRef.current,
      )
      frameMeshRef.current.castShadow = true
      frameMeshRef.current.receiveShadow = true

      if (
        frameSettings.passepartoutTopSize > 0 ||
        frameSettings.passepartoutRightSize > 0 ||
        frameSettings.passepartoutBottomSize > 0 ||
        frameSettings.passepartoutLeftSize > 0
      ) {
        passepartoutMeshRef.current = new THREE.Mesh(
          passepartoutGeometryRef.current,
          passepartoutMaterialRef.current,
        )
        passepartoutMeshRef.current.castShadow = false
        passepartoutMeshRef.current.receiveShadow = false
      }

      artworkMeshRef.current = new THREE.Mesh(
        artworkGeometryRef.current,
        artworkMaterialRef.current,
      )
      artworkMeshRef.current.castShadow = false
      artworkMeshRef.current.receiveShadow = false

      sceneRef.current.add(frameMeshRef.current, artworkMeshRef.current)

      if (
        passepartoutMeshRef.current &&
        (frameSettings.passepartoutTopSize > 0 ||
          frameSettings.passepartoutRightSize > 0 ||
          frameSettings.passepartoutBottomSize > 0 ||
          frameSettings.passepartoutLeftSize > 0)
      ) {
        sceneRef.current.add(passepartoutMeshRef.current)
      }

      // Create UV viewers
      frameUvViewerRef.current = new UVMapViewer(
        frameMeshRef.current,
        undefined,
        {
          strokeStyle: `red`,
          strokeWidth: 3,
        },
        canvasRef.current,
      )
      artworkUvViewerRef.current = new UVMapViewer(
        artworkMeshRef.current,
        undefined,
        {
          strokeStyle: `red`,
          strokeWidth: 3,
        },
        canvasRef.current,
      )

      fitCameraToFrame()

      // Update artwork image texture
      if (frameSettings.artworkUrl) {
        debounceUpdateArtworkTexture.current(frameSettings)
      }

      updateFrameTexture(true)

      // Create wall
      wallMaterialRef.current = new THREE.MeshBasicMaterial({
        map: wallTexture,
      })
      wallGeometryRef.current = new THREE.PlaneGeometry(wallWidth, wallHeight)
      wallMeshRef.current = new THREE.Mesh(
        wallGeometryRef.current,
        wallMaterialRef.current,
      )
      // Place directly behind the frame
      wallMeshRef.current.position.set(0, 0, -0.001)
      sceneRef.current.add(wallMeshRef.current)
    }, [camera.settings, isReady, width, height, wallTexture])

    // Update artwork image
    useEffect(() => {
      if (!frameSettings.artworkUrl) {
        setArtworkUrl(null)
        setArtworkTexture(null)
        return
      }

      if (artworkUrl != frameSettings.artworkUrl || !artworkTexture) {
        debounceUpdateArtworkTexture.current(frameSettings)
      }
    }, [
      frameSettings.artworkUrl,
      frameSettings.artworkWidth,
      frameSettings.artworkHeight,
    ])

    // Map artwork texture to artwork mesh
    useEffect(() => {
      if (!isReady || !artworkMaterialRef.current) {
        return
      }

      if (artworkTexture) {
        artworkMaterialRef.current.map = artworkTexture
        // @ts-expect-error Using null is necessary for Three.js to use texture instead of a solid color
        artworkMaterialRef.current.color = null
        artworkMaterialRef.current.needsUpdate = true

        return
      }

      artworkMaterialRef.current.map = null
      artworkMaterialRef.current.color = new THREE.Color('white')
      artworkMaterialRef.current.needsUpdate = true
    }, [isReady, artworkTexture])

    // Update frame color/texture image
    useEffect(() => {
      updateFrameTexture()
    }, [updateFrameTexture])

    // Map frame texture to frame mesh
    useEffect(() => {
      if (!isReady || !frameMaterialRef.current) {
        return
      }

      frameMaterialRef.current.map = frameDiffuseTexture ?? null
      // @ts-expect-error Using null is necessary for Three.js to use texture instead of a solid color
      frameMaterialRef.current.color = frameDiffuseTexture
        ? null
        : new THREE.Color('black')
      frameMaterialRef.current.needsUpdate = true
    }, [isReady, frameDiffuseTexture])

    // Update passepartout size
    useEffect(() => {
      if (!isReady || !sceneRef.current) {
        return
      }

      if (
        frameSettings.passepartoutTopSize > 0 ||
        frameSettings.passepartoutRightSize > 0 ||
        frameSettings.passepartoutBottomSize > 0 ||
        frameSettings.passepartoutLeftSize > 0
      ) {
        if (!passepartoutMaterialRef.current) {
          passepartoutMaterialRef.current = new THREE.MeshBasicMaterial({
            color: frameSettings.passepartoutColor,
            reflectivity: 0,
          })
        }

        if (!passepartoutMeshRef.current) {
          passepartoutMeshRef.current = new THREE.Mesh(
            passepartoutGeometryRef.current,
            passepartoutMaterialRef.current,
          )
          passepartoutMeshRef.current.receiveShadow = true

          sceneRef.current.add(passepartoutMeshRef.current)
        }

        return
      }

      if (passepartoutMeshRef.current) {
        sceneRef.current.remove(passepartoutMeshRef.current)
        passepartoutMeshRef.current = undefined
      }

      if (passepartoutMaterialRef.current) {
        passepartoutMaterialRef.current.dispose()
        passepartoutMaterialRef.current = undefined
      }
    }, [
      isReady,
      frameSettings.passepartoutTopSize,
      frameSettings.passepartoutRightSize,
      frameSettings.passepartoutBottomSize,
      frameSettings.passepartoutLeftSize,
    ])

    // Update passepartout color
    useEffect(() => {
      if (!isReady || !passepartoutMaterialRef.current) {
        return
      }

      passepartoutMaterialRef.current.setValues({
        color: frameSettings.passepartoutColor,
      })
    }, [isReady, frameSettings.passepartoutColor])

    // Update frame dimensions
    useEffect(() => {
      if (
        !isReady ||
        !frameGeometryRef.current ||
        !passepartoutGeometryRef.current ||
        !artworkGeometryRef.current
      ) {
        return
      }

      updateFrame(
        frameGeometryRef.current,
        passepartoutGeometryRef.current,
        artworkGeometryRef.current,
        frameSettings,
      )
      fitCameraToFrame()
    }, [
      isReady,
      frameSettings.artworkHeight,
      frameSettings.artworkWidth,
      frameSettings.borderTopSize,
      frameSettings.borderRightSize,
      frameSettings.borderBottomSize,
      frameSettings.borderLeftSize,
      frameSettings.passepartoutTopSize,
      frameSettings.passepartoutRightSize,
      frameSettings.passepartoutBottomSize,
      frameSettings.passepartoutLeftSize,
      frameSettings.inset,
    ])

    // Update width/height related stuff when necessary
    useEffect(() => {
      if (
        !isReady ||
        !rendererRef.current ||
        !cameraRef.current ||
        !width ||
        !height
      ) {
        return
      }

      cameraRef.current.aspect = width / height
      fitCameraToFrame()

      rendererRef.current.setSize(width, height)

      // UV debugging viewers
      if (debugFrameUv && frameUvViewerRef.current) {
        frameUvViewerRef.current.setPosition(width - width * 0.3, 0)
        frameUvViewerRef.current.setSize(width * 0.3, width * 0.3)
      }

      if (debugArtworkUv && artworkUvViewerRef.current) {
        artworkUvViewerRef.current.setSize(width * 0.3, width * 0.3)
      }
    }, [debugArtworkUv, debugFrameUv, isReady, width, height])

    // Calculate and refresh maximum object dimension for camera zoom
    useEffect(() => {
      if (!isReady || !frameMeshRef.current) {
        return
      }

      const size = getObjectSizeVector(frameMeshRef.current)
      const newMaxDimension = Math.max(size.x, size.y, size.z)

      debouncedSetMaxDimensionTarget.current(newMaxDimension)
    }, [
      frameSettings.artworkHeight,
      frameSettings.artworkWidth,
      frameSettings.borderBottomSize,
      frameSettings.borderLeftSize,
      frameSettings.borderRightSize,
      frameSettings.borderTopSize,
      frameSettings.passepartoutBottomSize,
      frameSettings.passepartoutLeftSize,
      frameSettings.passepartoutRightSize,
      frameSettings.passepartoutTopSize,
      isReady,
    ])

    // Update camera on maximum object dimension and object rotation changes
    useEffect(() => {
      if (!isReady) {
        return
      }

      fitCameraToFrame()
    }, [fitCameraToFrame, isReady])

    // Rerender when necessary
    useEffect(() => {
      if (
        !isReady ||
        !rendererRef.current ||
        !sceneRef.current ||
        !cameraRef.current
      ) {
        return
      }

      rendererRef.current.render(sceneRef.current, cameraRef.current)

      if (debugFrameUv && frameUvViewerRef.current) {
        frameUvViewerRef.current.render(rendererRef.current)
      }

      if (debugArtworkUv && artworkUvViewerRef.current) {
        artworkUvViewerRef.current.render(rendererRef.current)
      }
    }, [
      debugArtworkUv,
      debugFrameUv,
      isReady,
      frameSettings,
      artworkTexture,
      frameDiffuseTexture,
      rotation,
      maxDimension,
    ])

    return (
      <canvas
        ref={canvasRef}
        onMouseEnter={() => setRotationTarget(camera.rotationHover)}
        onMouseLeave={() => setRotationTarget(camera.rotationInitial)}
        onTouchStart={() => setRotationTarget(camera.rotationHover)}
        onTouchEnd={() => setRotationTarget(camera.rotationInitial)}
        className={cx('block select-none', className)}
        style={style}
      />
    )
  },
)

FrameViewer.displayName = 'FrameViewer'

export default FrameViewer
